import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  downloadMarketplaceSpreadsheet,
  getMarketplaceProductRows,
} from "../Functions/CreateMarketplaceSpreadsheet.js";

// Match the primary and secondary actions in HomePageHeader.
const primaryButtonStyle = { background: "#45AA45", color: "white" };
const secondaryButtonStyle = { background: "white", color: "#45AA45" };

export default function MarketplaceProductDownload({
  foodItems,
  userInfo,
  pickupLocationDetails,
  loadError = false,
  loading = false,
  loadProducts,
  farmNames = [],
}) {
  const [open, setOpen] = useState(false);
  const [selectedFarms, setSelectedFarms] = useState([]);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const farms = [
    ...new Set([...farmNames, ...foodItems.map((food) => food.farmName)]),
  ].sort((first, second) => first.localeCompare(second));
  const selectedFarmCount = farms.filter((farm) =>
    selectedFarms.includes(farm),
  ).length;
  const downloadDisabled = loadError || selectedFarmCount === 0 || downloading;

  function handleOpen() {
    setSelectedFarms(farms);
    setError("");
    setOpen(true);
  }

  function handleFarmChange(farm, checked) {
    setSelectedFarms((selected) =>
      checked ? [...selected, farm] : selected.filter((name) => name !== farm),
    );
    setError("");
  }

  async function handleDownload() {
    setDownloading(true);
    setError("");
    try {
      const products = loadProducts
        ? await loadProducts(selectedFarms)
        : foodItems;
      const productRows = getMarketplaceProductRows(
        products,
        selectedFarms,
        userInfo,
        pickupLocationDetails,
      );
      await downloadMarketplaceSpreadsheet(productRows);
      setOpen(false);
    } catch {
      setError("The spreadsheet could not be downloaded. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="Marketplace-Product-Download">
      <Button
        style={loading ? undefined : primaryButtonStyle}
        size="large"
        variant="contained"
        onClick={handleOpen}
        disabled={loading}
      >
        Download product list
      </Button>
      <Dialog
        open={open}
        onClose={() => !downloading && setOpen(false)}
        disableEscapeKeyDown={downloading}
        aria-labelledby="marketplace-download-title"
        aria-describedby="marketplace-download-description"
      >
        <DialogTitle
          id="marketplace-download-title"
          style={{ padding: "16px 24px 0px" }}
        >
          Download marketplace products
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="marketplace-download-description">
            Choose farms to include in an Excel spreadsheet. Includes all
            current products from those farms in this marketplace, regardless of
            page filters.
          </DialogContentText>
          {loadError ? (
            <Alert severity="error">
              Some products could not be loaded. Reload the marketplace before
              downloading the product list.
            </Alert>
          ) : (
            <>
              {farms.length === 0 ? (
                <DialogContentText>
                  There are no products to download for this marketplace.
                </DialogContentText>
              ) : (
                <FormGroup aria-label="Farms to include">
                  <FormControlLabel
                    label="Select all farms"
                    control={
                      <Checkbox
                        disabled={downloading}
                        checked={selectedFarmCount === farms.length}
                        indeterminate={
                          selectedFarmCount > 0 &&
                          selectedFarmCount < farms.length
                        }
                        onChange={(event) =>
                          setSelectedFarms(event.target.checked ? farms : [])
                        }
                      />
                    }
                  />
                  {farms.map((farm) => (
                    <FormControlLabel
                      key={farm}
                      label={farm}
                      control={
                        <Checkbox
                          disabled={downloading}
                          checked={selectedFarms.includes(farm)}
                          onChange={(event) =>
                            handleFarmChange(farm, event.target.checked)
                          }
                        />
                      }
                    />
                  ))}
                </FormGroup>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            style={downloading ? undefined : secondaryButtonStyle}
            size="large"
            variant="outlined"
            disabled={downloading}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            style={downloadDisabled ? undefined : primaryButtonStyle}
            size="large"
            variant="contained"
            disabled={downloadDisabled}
            onClick={handleDownload}
          >
            {downloading ? "Downloading…" : "Download Excel"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
